<template>
  <div :class="flat ? '' : 'rounded-lg border bg-card shadow-sm'">
    <div class="flex items-center justify-between" :class="flat ? 'mb-2' : 'border-b px-4 py-2.5'">
      <h3 class="text-sm font-semibold">Conversations liées <span class="text-muted-foreground">({{ issues.length }})</span></h3>
      <UiButton size="sm" variant="brand" @click="openCreate">
        <PlusIcon class="h-4 w-4" /> Créer
      </UiButton>
    </div>
    <div v-if="issues.length" :class="flat ? 'divide-y overflow-hidden rounded-md border' : 'divide-y'">
      <button
        v-for="issue in issues"
        :key="issue.id"
        class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-accent/50"
        @click="openIssue(issue)"
      >
        <UiBadge variant="secondary">{{ statusLabel(issue.status) }}</UiBadge>
        <span class="min-w-0 flex-1 truncate font-medium">{{ issue.title }}</span>
        <span v-if="linkVia(issue)" class="shrink-0 text-xs text-muted-foreground">{{ linkVia(issue) }}</span>
        <UiAvatar v-if="issue.owner" :name="issue.owner.name || issue.owner.email" />
      </button>
    </div>

    <IssueDetail
      :open="detailOpen"
      :issue="selectedIssue"
      :users="users"
      :labels="labels"
      @close="closeDetail"
      @updated="onIssueUpdated"
      @labels-changed="loadLabels"
    />
  </div>
</template>

<script>
import { PlusIcon } from "@heroicons/vue/outline";
import IssueDetail from "@/components/board/IssueDetail.vue";
import UiAvatar from "@/components/ui/UiAvatar.vue";
import UiBadge from "@/components/ui/UiBadge.vue";
import UiButton from "@/components/ui/UiButton.vue";
import { ISSUE_STATUSES, useBoardService } from "@/composables/boardService";

export default {
  name: "IssuesLinkPanel",
  components: { IssueDetail, UiAvatar, UiBadge, UiButton, PlusIcon },
  props: {
    type: { type: String, required: true, validator: (v) => ["invoice", "transaction", "company"].includes(v) },
    id: { type: [String, Number], required: true },
    flat: { type: Boolean, default: false },
    defaultTitle: { type: String, default: "" },
  },
  setup() {
    return { board: useBoardService() };
  },
  data() {
    return {
      issues: [],
      users: [],
      labels: [],
      detailOpen: false,
      selectedIssue: null,
    };
  },
  watch: {
    id() {
      this.loadIssues();
    },
  },
  async mounted() {
    await Promise.all([this.loadIssues(), this.loadUsers(), this.loadLabels()]);
  },
  methods: {
    filterParam() {
      return { [`${this.type}_id`]: this.id };
    },
    statusLabel(id) {
      const status = ISSUE_STATUSES.find((s) => s.id === id);
      return status ? status.label : id;
    },
    // Pour un fournisseur, indique si la conversation est liée indirectement
    // (via une facture ou une transaction du fournisseur) plutôt que directement.
    linkVia(issue) {
      if (this.type !== "company") return "";
      const companyId = String(this.id);
      if ((issue.companies || []).some((c) => String(c.id) === companyId)) return "";
      if ((issue.invoices || []).some((i) => String(i.company_id) === companyId)) return "via facture";
      return "via transaction";
    },
    async loadIssues() {
      if (!this.id) return;
      this.issues = await this.board.listIssues(this.filterParam());
    },
    async loadUsers() {
      this.users = await this.board.listUsers();
    },
    async loadLabels() {
      this.labels = await this.board.listLabels();
    },
    async openCreate() {
      const created = await this.board.createIssue({
        title: this.defaultTitle || "Nouvelle conversation",
        [`${this.type}_id`]: this.id,
      });
      await this.loadIssues();
      this.openIssue(created);
    },
    async openIssue(issue) {
      this.selectedIssue = await this.board.getIssue(issue.id);
      this.detailOpen = true;
    },
    closeDetail() {
      this.detailOpen = false;
      this.selectedIssue = null;
      this.loadIssues();
    },
    onIssueUpdated(issue) {
      this.selectedIssue = issue;
      this.loadIssues();
    },
  },
};
</script>
